/**
 * The Press Desk — broadcast-grade results map.
 * Renders pre-simplified GeoJSON as clickable, party-colored SVG shapes.
 * Party color = single source of visual meaning.
 * Features: constituency code labels, zoom in/out controls.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { projectCollection, GeoCollection, Projected } from "@/lib/geo";
import { partyColor, party } from "@/lib/parties";
import { Plus, Minus, RotateCcw } from "lucide-react";

interface SeatLike {
  code: string;
  name: string;
  party: string;
  winner: string;
  majority: number;
}

export interface LiveSeat {
  status: "leading" | "won" | "pending";
  party: string;
  official?: boolean;
  majority?: number;
}

interface Props {
  geoUrl: string;
  results: Record<string, SeatLike>;
  selectedCode?: string;
  onSelect: (code: string | null) => void;
  onHover?: (code: string | null) => void;
  /** When provided, the map renders election-night live status instead of 2022 colors. */
  live?: Record<string, LiveSeat>;
  /** When set, dim seats whose party doesn't match this filter. */
  partyFilter?: string | null;
}

export default function ConstituencyMap({
  geoUrl,
  results,
  selectedCode,
  onSelect,
  onHover,
  live,
  partyFilter,
}: Props) {
  const [fc, setFc] = useState<GeoCollection | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; seat: SeatLike } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const lastPinchCenter = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    let alive = true;
    setFc(null);
    fetch(geoUrl)
      .then((r) => r.json())
      .then((d) => {
        if (alive) setFc(d);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [geoUrl]);

  const proj = useMemo(() => (fc ? projectCollection(fc, 1000, 14) : null), [fc]);

  // Reset zoom/pan when switching geo
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [geoUrl]);

  // Scroll-wheel zoom handler
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => {
      const newZ = Math.max(1, Math.min(z * delta, 5));
      if (newZ === 1) setPan({ x: 0, y: 0 });
      return newZ;
    });
  }, []);

  // Attach wheel listener with passive: false to allow preventDefault
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Pinch-to-zoom touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsTouching(true);
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.hypot(dx, dy);
      lastPinchCenter.current = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastPinchDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const scale = dist / lastPinchDist.current;
      lastPinchDist.current = dist;

      setZoom((z) => {
        const newZ = Math.max(1, Math.min(z * scale, 5));
        if (newZ === 1) setPan({ x: 0, y: 0 });
        return newZ;
      });

      // Pan while pinching
      const center = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
      if (lastPinchCenter.current) {
        const panDx = center.x - lastPinchCenter.current.x;
        const panDy = center.y - lastPinchCenter.current.y;
        setPan((p) => ({ x: p.x + panDx, y: p.y + panDy }));
      }
      lastPinchCenter.current = center;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    lastPinchDist.current = null;
    lastPinchCenter.current = null;
    setIsTouching(false);
  }, []);

  if (!proj) {
    return (
      <div className="flex aspect-square w-full items-center justify-center text-muted-foreground">
        <div className="animate-pulse text-sm">Loading constituency map…</div>
      </div>
    );
  }

  const handleEnter = (s: Projected, e: React.MouseEvent) => {
    setHovered(s.code);
    onHover?.(s.code);
    const seat = results[s.code];
    const rect = wrapRef.current?.getBoundingClientRect();
    if (seat && rect) {
      setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, seat });
    }
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.4, 5));
  const handleZoomOut = () => {
    setZoom((z) => {
      const newZ = Math.max(z / 1.4, 1);
      if (newZ === 1) setPan({ x: 0, y: 0 });
      return newZ;
    });
  };
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Pan handlers (mouse/single-touch drag when zoomed)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoom <= 1) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setPan({ x: panStart.current.panX + dx, y: panStart.current.panY + dy });
  };
  const handlePointerUp = () => {
    setIsPanning(false);
  };

  // Show labels when zoom >= 1.5
  const showLabels = zoom >= 1.5;

  return (
    <div ref={wrapRef} className="relative w-full h-full overflow-hidden" style={{ minHeight: '200px' }}>
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-30 flex flex-col gap-1">
        <button
          onClick={handleZoomIn}
          className="flex h-8 w-8 items-center justify-center rounded bg-card border border-border shadow-sm hover:bg-secondary transition-colors active:scale-95"
          aria-label="Zoom in"
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          onClick={handleZoomOut}
          className="flex h-8 w-8 items-center justify-center rounded bg-card border border-border shadow-sm hover:bg-secondary transition-colors active:scale-95"
          aria-label="Zoom out"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          onClick={handleReset}
          className="flex h-8 w-8 items-center justify-center rounded bg-card border border-border shadow-sm hover:bg-secondary transition-colors active:scale-95"
          aria-label="Reset zoom"
          title="Reset to full view"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Zoom level indicator */}
      {zoom > 1 && (
        <div className="absolute top-2 left-2 z-30 rounded bg-card/80 border border-border px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
          {Math.round(zoom * 100)}%
        </div>
      )}

      <div
        style={{
          cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
          touchAction: "none",
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <svg
          viewBox={`0 0 ${proj.width} ${proj.height}`}
          className="mx-auto block select-none"
          style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
          preserveAspectRatio="xMidYMid meet"
          onClick={(e) => {
            if (e.target === e.currentTarget) onSelect(null);
          }}
        >
          {/* Hatched pattern definitions for "leading" seats */}
          <defs>
            {Array.from(new Set(
              proj.shapes
                .map(s => live?.[s.code])
                .filter(lv => lv && lv.status === "leading")
                .map(lv => lv!.party)
            )).map(p => {
              const c = partyColor(p);
              return (
                <pattern
                  key={`hatch-${p}`}
                  id={`hatch-${p}`}
                  patternUnits="userSpaceOnUse"
                  width="6"
                  height="6"
                  patternTransform="rotate(45)"
                >
                  <rect width="6" height="6" fill={c} fillOpacity="0.3" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke={c} strokeWidth="3" strokeOpacity="0.8" />
                </pattern>
              );
            })}
          </defs>
          <g
            transform={`translate(${proj.width / 2 + pan.x * (proj.width / (wrapRef.current?.clientWidth || proj.width))}, ${proj.height / 2 + pan.y * (proj.height / (wrapRef.current?.clientHeight || proj.height))}) scale(${zoom}) translate(${-proj.width / 2}, ${-proj.height / 2})`}

          >
          {[...proj.shapes].sort((a, b) => {
            if (a.code === selectedCode) return 1;
            if (b.code === selectedCode) return -1;
            return 0;
          }).map((s) => {
            const seat = results[s.code];
            const lv = live?.[s.code];
            let fill: string;
            let fillOpacity: number;
            const MAIN_PARTIES = ["BN", "PH", "PN", "MUDA", "BERSAMA"];
            const matchesFilter = (p: string) => {
              if (!partyFilter) return true;
              if (partyFilter === "OTHERS") return !MAIN_PARTIES.includes(p);
              return p === partyFilter;
            };
            if (live) {
              if (lv && lv.status !== "pending") {
                if (partyFilter && !matchesFilter(lv.party)) {
                  fill = "#d6d3cd";
                  fillOpacity = 0.5;
                } else if (lv.status === "leading") {
                  fill = `url(#hatch-${lv.party})`;
                  fillOpacity = 1;
                } else {
                  fill = partyColor(lv.party);
                  fillOpacity = 1;
                }
              } else {
                if (partyFilter && seat && seat.party !== "OTHER_DIM" && !matchesFilter(seat.party)) {
                  fill = "#d6d3cd";
                  fillOpacity = 0.4;
                } else {
                  fill = "#ffffff";
                  fillOpacity = 1;
                }
              }
            } else {
              fill = seat ? partyColor(seat.party) : "#d6d3cd";
              fillOpacity = 1;
            }
            const isSel = selectedCode === s.code;
            const isHov = hovered === s.code;
            if (!live) fillOpacity = 1;
            const leading = live && lv?.status === "leading";
            const isPendingSeat = live && (!lv || lv.status === "pending");
            if (isPendingSeat && isHov && !isSel) fill = "#fbe9ec";
            const stroke = isSel ? "#000000" : isPendingSeat ? "#b9b3a8" : "#faf8f4";
            const strokeW = isSel ? 3.5 : isPendingSeat ? 0.9 : 0.7;
            return (
              <path
                key={s.code}
                d={s.path}
                fill={fill}
                fillOpacity={isSel ? Math.max(fillOpacity, 0.95) : fillOpacity}
                stroke={stroke}
                strokeWidth={strokeW}
                paintOrder={isSel ? "fill" : undefined}
                className={`cursor-pointer ${isSel ? "[paint-order:fill]" : ""}`}
                style={{
                  ...(isSel && !isTouching ? { filter: "drop-shadow(0 0 2px rgba(0,0,0,0.4))" } : {}),
                }}
                onMouseEnter={(e) => handleEnter(s, e)}
                onMouseMove={(e) => handleEnter(s, e)}
                onMouseLeave={() => {
                  setHovered(null);
                  setTip(null);
                  onHover?.(null);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setTip(null);
                  setHovered(null);
                  onSelect(selectedCode === s.code ? null : s.code);
                }}
              />
            );
          })}

          {/* Constituency code labels — shown when zoomed in */}
          {showLabels && proj.shapes.map((s) => (
            <text
              key={`label-${s.code}`}
              x={s.cx}
              y={s.cy}
              textAnchor="middle"
              dominantBaseline="central"
              className="pointer-events-none select-none"
              style={{
                fontSize: `${Math.max(8, 11 / zoom)}px`,
                fontWeight: 700,
                fill: selectedCode === s.code ? "#000" : "#333",
                stroke: "#fff",
                strokeWidth: 2.5,
                paintOrder: "stroke",
                opacity: 0.9,
              }}
            >
              {s.code}
            </text>
          ))}
          </g>
        </svg>
      </div>

      {/* Tooltip */}
      {tip && (
        <div
          className="pointer-events-none absolute z-20 hidden w-56 rounded-md border border-border bg-card p-3 shadow-lg lg:block"
          style={{
            left: Math.min(tip.x + 14, (wrapRef.current?.clientWidth ?? 0) - 230),
            top: tip.y + 14,
          }}
        >
          <div className="flex items-center gap-2">
            {(!live || live[tip.seat.code]?.status === "won") && (
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: partyColor(tip.seat.party) }}
              />
            )}
            <span className="kicker text-muted-foreground">
              {tip.seat.code}{(!live || live[tip.seat.code]?.status === "won") && ` · ${party(tip.seat.party).name}`}
            </span>
          </div>
          <div className="mt-1 font-serif-ed text-lg font-semibold leading-tight">
            {tip.seat.name}
          </div>
          <div className="mt-0.5 text-sm text-foreground/80">{tip.seat.winner}</div>
          {live ? (
            <>
              <div className="mt-1 text-xs font-bold uppercase tracking-wide" style={{ color: partyColor(tip.seat.party) }}>
                {live[tip.seat.code]?.status === "won"
                  ? "Declared"
                  : live[tip.seat.code]?.status === "leading"
                    ? "Leading"
                    : "Pending"}
              </div>
              {live[tip.seat.code]?.official && live[tip.seat.code]?.majority != null && (
                <div className="mt-0.5 text-xs text-muted-foreground tnum">
                  Majority {live[tip.seat.code]!.majority!.toLocaleString()}
                </div>
              )}
            </>
          ) : (
            <div className="mt-1 text-xs text-muted-foreground tnum">
              Majority {tip.seat.majority.toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
